import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";
import { Calendar, User, ArrowLeft } from "lucide-react";
import blogsData from "../data/blogs.json";
import { getSaasUrl } from "../utils/saasUrl";

const BlogPost = () => {
  const { slug } = useParams();
  const post = blogsData.find((p) => p.slug === slug);

  // Replace trial URLs with environment-specific SaaS URLs
  const processContent = (content) => {
    const saasUrl = getSaasUrl();
    return content
      .replace(/href='\/trial'/g, `href='${saasUrl}'`)
      .replace(/href="\/trial"/g, `href="${saasUrl}"`)
      .replace(/target='_blank'/g, "target='_blank'");
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Post Not Found</h1>
          <Link to="/resources" className="text-orange-500 hover:underline">
            Back to Resources
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-4 sm:px-6 lg:px-8 py-12">
        <Link
          to="/resources"
          className="inline-flex items-center text-orange-500 hover:text-orange-600 mb-8"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Resources
        </Link>

        <article className="bg-white rounded-xl shadow-lg p-8">
          <header className="mb-8">
            <h1 className="text-4xl font-bold mb-4">{post.title}</h1>
            <div className="flex items-center text-gray-500 mb-4">
              <Calendar className="h-4 w-4 mr-2" />
              <span>
                {new Date(post.date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </span>
              <span className="mx-2">•</span>
              <User className="h-4 w-4 mr-1" />
              <span>{post.author}</span>
              <span className="mx-2">•</span>
              <span>{post.readTime}</span>
            </div>
            {post.tags && (
              <div className="flex flex-wrap gap-2 mb-6">
                {post.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </header>

          <div
            className="prose prose-xl max-w-none text-lg leading-relaxed"
            style={{ fontSize: '1.125rem', lineHeight: '1.75' }}
            dangerouslySetInnerHTML={{ __html: processContent(post.content) }}
          />
        </article>

        {/* Related Posts */}
        <section className="mt-12">
          <h2 className="text-2xl font-medium mb-6">
            More{" "}
            {post.category === "blogs"
              ? "Blogs"
              : post.category === "help"
              ? "Guides"
              : "Updates"}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {blogsData
              .filter((p) => p.category === post.category && p.id !== post.id)
              .slice(0, 3)
              .map((relatedPost) => (
                <Link
                  key={relatedPost.id}
                  to={`/${relatedPost.category}/${relatedPost.slug}`}
                  className="card hover:shadow-xl transition-shadow"
                >
                  <h3 className="text-base font-medium mb-2 text-gray-900">
                    {relatedPost.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">
                    {relatedPost.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      {relatedPost.readTime}
                    </span>
                    <span className="text-orange-500 text-sm font-medium">
                      Read More →
                    </span>
                  </div>
                </Link>
              ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default BlogPost;
